import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [companyCode, setCompanyCode] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!backendUrl) {
      setErrorMessage('Backend URL is not configured.');
      return;
    }

    if (!isSuperAdmin && !companyCode.trim()) {
      setErrorMessage('Please enter your company code.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_code: isSuperAdmin ? null : companyCode.trim(),
          email: email.trim(),
          password
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || 'Login failed. Please check your credentials.');
      }

      const data = await response.json();
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('user_profile', JSON.stringify(data.user));
      if (data.company) {
        localStorage.setItem('company_profile', JSON.stringify(data.company));
      } else {
        localStorage.removeItem('company_profile');
      }

      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(error.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Overlay */}
      <div 
        className="hidden lg:flex lg:w-[60%] relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1759272548470-d0686d071036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB0cnVjayUyMHNoaXBwaW5nJTIwY29udGFpbmVyfGVufDB8fHx8MTc3MTY5ODkzMnww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-blue/90 to-primary-blue/50"></div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-accent-blue rounded-lg flex items-center justify-center">
              <Truck className="w-7 h-7" />
            </div>
            <span className="text-3xl font-bold font-manrope">LogisticsHub</span>
          </div>
          <h1 className="text-5xl font-bold font-manrope mb-6 leading-tight">
            Streamline Your<br />Logistics Operations
          </h1>
          <p className="text-xl text-white/90 font-ibm leading-relaxed">
            Manage shipments, track deliveries, and optimize<br />your fleet operations all in one powerful platform.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-text-dark font-manrope mb-2">Welcome Back</h2>
            <p className="text-text-secondary font-ibm">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {errorMessage && (
              <div
                data-testid="login-error-message"
                className="rounded-lg border border-accent-red/40 bg-accent-red/10 px-4 py-3 text-sm text-accent-red font-ibm"
              >
                {errorMessage}
              </div>
            )}

            {/* Company Code */}
            <div className="space-y-2">
              <Label htmlFor="company-code" className="text-text-dark font-ibm font-medium">
                Company Code
              </Label>
              <Input
                id="company-code"
                type="text"
                data-testid="company-code-input"
                placeholder="Enter company code"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                disabled={isSuperAdmin}
                className="h-12 border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              <p data-testid="company-code-help" className="text-xs text-text-secondary font-ibm">
                {isSuperAdmin ? 'Company code not required for Super Admin.' : 'Required for company users.'}
              </p>
            </div>

            {/* Super Admin Toggle */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-text-secondary font-ibm">
                <input
                  type="checkbox"
                  data-testid="super-admin-toggle"
                  checked={isSuperAdmin}
                  onChange={(e) => setIsSuperAdmin(e.target.checked)}
                  className="h-4 w-4 text-accent-blue border-gray-300 rounded focus:ring-accent-blue"
                />
                Login as Super Admin
              </label>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-text-dark font-ibm font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                placeholder="your.email@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-text-dark font-ibm font-medium">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>

            {/* Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember"
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  className="h-4 w-4 text-accent-blue border-gray-300 rounded focus:ring-accent-blue"
                />
                <label htmlFor="remember" className="ml-2 text-sm text-text-secondary font-ibm">
                  Remember me
                </label>
              </div>
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-sm text-accent-blue hover:text-accent-blue/80 font-ibm font-medium"
              >
                Forgot password?
              </Link>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              className="w-full h-12 bg-accent-blue hover:bg-accent-blue/90 text-white text-base font-semibold font-ibm rounded-lg transition-colors disabled:opacity-70"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-text-secondary font-ibm">
              Don't have an account?{' '}
              <a
                href="#"
                data-testid="contact-admin-link"
                className="text-accent-blue hover:text-accent-blue/80 font-medium"
              >
                Contact Administrator
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
